/**
 * storeRules — Rules Engine V1 de CELMAXX.
 *
 * Capa central de decisiones operativas por tienda.
 * Los módulos NO deben leer storeConfig directamente para decidir si algo está permitido.
 * Deben llamar a estas funciones.
 *
 * Propiedades:
 * - Funciones puras — sin React, hooks, Supabase, Dexie ni Zustand
 * - Devuelven RuleResult en acciones visibles (para mostrar razón en UI)
 * - Devuelven boolean/number en utilidades internas
 *
 * Uso:
 *   const cfg = getStoreConfig(cajero?.storeConfig)
 *   const rule = canUseCredit(cfg)
 *   if (!rule.allowed) showToast(rule.reason)
 */

import type { StoreConfig } from './storeConfig'

// ── RuleResult ───────────────────────────────────────────────────────────────

export interface RuleResult {
  allowed: boolean
  reason?: string
  severity?: 'info' | 'warning' | 'danger'
}

const ALLOWED: RuleResult = { allowed: true }

function blocked(reason: string, severity: RuleResult['severity'] = 'info'): RuleResult {
  return { allowed: false, reason, severity }
}

// ── VENTAS ───────────────────────────────────────────────────────────────────

export function canUseCredit(config: StoreConfig): RuleResult {
  return config.sales.allow_credit
    ? ALLOWED
    : blocked('Credito desactivado para esta tienda', 'warning')
}

export function canCreateLayaway(config: StoreConfig): RuleResult {
  return config.sales.allow_layaway
    ? ALLOWED
    : blocked('Apartados desactivados para esta tienda', 'warning')
}

export function canCreateQuote(config: StoreConfig): RuleResult {
  return config.sales.allow_quote
    ? ALLOWED
    : blocked('Cotizaciones desactivadas para esta tienda', 'info')
}

export function canCreateOrder(config: StoreConfig): RuleResult {
  return config.sales.allow_order
    ? ALLOWED
    : blocked('Pedidos desactivados para esta tienda', 'info')
}

export function canApplyDiscount(config: StoreConfig): RuleResult {
  return config.sales.allow_discount
    ? ALLOWED
    : blocked('Descuentos desactivados para esta tienda', 'warning')
}

/**
 * Indica si se requiere PIN para aplicar un descuento de cierto porcentaje.
 * No devuelve RuleResult porque es una verificacion interna, no una accion visible.
 */
export function requiresPinForDiscount(config: StoreConfig, discountPercent: number): boolean {
  if (!config.sales.require_pin_for_discount) return false
  return discountPercent >= config.sales.discount_pin_threshold_percent
}

// ── POSTVENTA ────────────────────────────────────────────────────────────────

export function canCreateWarrantyFromPos(config: StoreConfig): RuleResult {
  return config.warranties.allow_warranty_from_pos
    ? ALLOWED
    : blocked('Garantias desactivadas para esta tienda', 'info')
}

export function canUseStoreCredit(config: StoreConfig): RuleResult {
  return config.postSale.allow_store_credit
    ? ALLOWED
    : blocked('Saldo a favor desactivado para esta tienda', 'info')
}

export function canCancelSale(config: StoreConfig): RuleResult {
  return config.postSale.allow_cancel_sale
    ? ALLOWED
    : blocked('Cancelacion de ventas desactivada para esta tienda', 'danger')
}

export function canReturnSale(config: StoreConfig): RuleResult {
  return config.postSale.allow_return_sale
    ? ALLOWED
    : blocked('Devoluciones desactivadas para esta tienda', 'warning')
}

// ── CAJA ─────────────────────────────────────────────────────────────────────

export function canCashIn(config: StoreConfig): RuleResult {
  return config.cash.allow_cash_in
    ? ALLOWED
    : blocked('Entradas de caja desactivadas para esta tienda', 'warning')
}

export function canCashOut(config: StoreConfig): RuleResult {
  return config.cash.allow_cash_out
    ? ALLOWED
    : blocked('Salidas de caja desactivadas para esta tienda', 'warning')
}

export function canCloseShift(_config: StoreConfig): RuleResult {
  // El cierre de turno siempre es permitido — nunca bloquear operaciones de seguridad
  return ALLOWED
}

/** Indica si el cierre de turno debe incluir conteo de efectivo */
export function requiresCashCount(config: StoreConfig): boolean {
  return config.cash.require_cash_count
}

/** Indica si el cierre de turno debe incluir nombre/firma del cajero */
export function requiresCloseSignature(config: StoreConfig): boolean {
  return config.cash.require_close_signature
}

/** Indica si el cierre de turno debe incluir motivo cuando hay diferencia */
export function requiresDifferenceReason(config: StoreConfig): boolean {
  return config.cash.require_difference_reason
}

// ── INVENTARIO ───────────────────────────────────────────────────────────────

/** Indica si la tienda permite vender con stock negativo */
export function canAllowNegativeStock(config: StoreConfig): boolean {
  return config.inventory.allow_negative_stock
}

/** Indica si los carritos en espera deben descontar stock visual */
export function shouldReserveCartStock(config: StoreConfig): boolean {
  return config.inventory.reserve_cart_stock
}

/** TTL de carritos en espera en horas */
export function getHeldCartTtlHours(config: StoreConfig): number {
  return config.inventory.held_cart_ttl_hours
}

/** TTL de carritos en espera en milisegundos (para Date comparisons) */
export function getHeldCartTtlMs(config: StoreConfig): number {
  return config.inventory.held_cart_ttl_hours * 60 * 60 * 1000
}

// ── TALLER / REPARACIONES ────────────────────────────────────────────────────

export function canAccessRepairs(config: StoreConfig): RuleResult {
  return config.repairs.enabled
    ? ALLOWED
    : blocked('Taller no habilitado para esta tienda.', 'info')
}

// ── TICKETS ──────────────────────────────────────────────────────────────────

/** Indica si el ticket debe mostrar el desglose de IVA */
export function shouldShowTaxBreakdown(config: StoreConfig): boolean {
  return config.fiscal.show_tax_breakdown
}

/** Indica si se debe imprimir ticket adicional al crear garantia */
export function shouldPrintWarrantyReceipt(config: StoreConfig): boolean {
  return config.warranties.print_warranty_receipt
}
